import { useAuth } from '../../contexts/AuthContext';
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Download, Calendar, Briefcase, Loader2, AlertCircle } from 'lucide-react';
import * as XLSX from 'xlsx';
import { fetchFinalSettlementReport } from '../../services/reportsApi';

const FinalSettlementView = ({ departmentsList = [] }) => {
  const { hasPermission } = useAuth();
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth(); // 0-indexed (Jan = 0, Apr = 3)
  
  // Financial year starting year for current date (e.g. Sept 2026 -> 2026)
  const currentFyStart = currentMonth >= 3 ? currentYear : currentYear - 1;
  const initialFy = `${currentFyStart}-${currentFyStart + 1}`;

  const [financialYear, setFinancialYear] = useState(initialFy);
  const [department, setDepartment] = useState('ALL');
  const [reportRows, setReportRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const abortRef = useRef(null);

  // Generate options for financial years starting from 2025-26 up to current FY
  const fyOptions = useMemo(() => {
    const startYr = 2025;
    const endYr = Math.max(2025, currentFyStart);
    const options = [];
    for (let i = startYr; i <= endYr; i++) {
      options.push(`${i}-${i + 1}`);
    }
    return options;
  }, [currentFyStart]);

  const startYear = parseInt(financialYear.split('-')[0]) || 2025;
  const endYear = parseInt(financialYear.split('-')[1]) || (startYear + 1);

  useEffect(() => {
    const loadData = async () => {
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setLoading(true);
      setError('');

      try {
        const res = await fetchFinalSettlementReport(startYear, department, controller.signal);
        if (!controller.signal.aborted) {
          setReportRows(res.rows || []);
        }
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Error fetching final settlement data:', err);
          setError(err.message || 'Failed to load final settlement report data');
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    loadData();
  }, [startYear, department]);

  const filteredData = reportRows;

  const calculateTotal = (key) => filteredData.reduce((acc, row) => acc + (row[key] || 0), 0);
  const totals = {
    apr: calculateTotal('apr'),
    may: calculateTotal('may'),
    jun: calculateTotal('jun'),
    jul: calculateTotal('jul'),
    aug: calculateTotal('aug'),
    sep: calculateTotal('sep'),
    oct: calculateTotal('oct'),
    nov: calculateTotal('nov'),
    dec: calculateTotal('dec'),
    jan: calculateTotal('jan'),
    feb: calculateTotal('feb'),
    mar: calculateTotal('mar'),
    total: calculateTotal('total')
  };

  const handleExport = () => {
    // Top header lines
    const titleRow = [['PUNE CANTONMENT BOARD']];
    const subtitleRow = [[`FINAL SETTLEMENT FOR THE YEAR ${startYear}-${endYear.toString().slice(-2)}`]];
    
    // Column headers
    const headers = [
      '', 
      `01-04-${startYear}`, `01-05-${startYear}`, `01-06-${startYear}`, `01-07-${startYear}`,
      `01-08-${startYear}`, `01-09-${startYear}`, `01-10-${startYear}`, `01-11-${startYear}`,
      `01-12-${startYear}`, `01-01-${endYear}`, `01-02-${endYear}`, `01-03-${endYear}`,
      'TOTAL'
    ];

    // Data rows
    const dataRows = filteredData.map(row => [
      row.dept,
      row.apr || '', row.may || '', row.jun || '', row.jul || '',
      row.aug || '', row.sep || '', row.oct || '', row.nov || '',
      row.dec || '', row.jan || '', row.feb || '', row.mar || '',
      row.total
    ]);

    // Total row
    const totalRow = [
      'TOTAL',
      totals.apr || 0, totals.may || 0, totals.jun || 0, totals.jul || 0,
      totals.aug || 0, totals.sep || 0, totals.oct || 0, totals.nov || 0,
      totals.dec || 0, totals.jan || 0, totals.feb || 0, totals.mar || 0,
      totals.total
    ];

    // Signatures
    const signaturesRow1 = ['Prepared by', '', '', '', '', '', 'Checked by', '', '', '', '', '', ''];
    const signaturesRow2 = ['', '', '', '', '', '', '', '', '', '', 'Chief Accountant', '', ''];

    // Combine all
    const sheetData = [
      ...titleRow,
      ...subtitleRow,
      headers,
      ...dataRows,
      totalRow,
      [], [], // Empty rows
      signaturesRow1,
      [], [], // Empty rows
      signaturesRow2
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
    
    // Merge cells for titles
    worksheet['!merges'] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: 13 } }, // PUNE CANTONMENT BOARD
      { s: { r: 1, c: 0 }, e: { r: 1, c: 13 } }  // FINAL SETTLEMENT FOR THE YEAR...
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Final Settlement');
    
    XLSX.writeFile(workbook, `Final_Settlement_${financialYear}.xlsx`);
  };

  const getThClass = () => `border border-gray-400 px-2 py-2 text-xs font-semibold text-center whitespace-nowrap bg-white text-gray-700`;
  const getTdClass = (isNumeric = true) => `border border-gray-400 px-2 py-2 text-xs whitespace-nowrap ${isNumeric ? 'text-right' : 'text-left'}`;
  const formatCell = (val) => val === null || val === 0 ? '' : val;

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Filters and Actions */}
      <div className="p-4 border-b border-gray-200 flex flex-wrap gap-4 items-end justify-between bg-gray-50">
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1 flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              Financial Year
            </label>
            <select
              value={financialYear}
              onChange={(e) => setFinancialYear(e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white min-w-[140px]"
            >
              {fyOptions.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1 flex items-center gap-1">
              <Briefcase className="w-3 h-3" />
              Department
            </label>
            <select
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white min-w-[150px]"
            >
              <option value="ALL">All Departments</option>
              {departmentsList && departmentsList.map((d) => (
                <option key={d.id || d.code || d.name} value={d.code || d.name}>
                  {d.name} {d.code && d.code !== d.name ? `(${d.code})` : ''}
                </option>
              ))}
            </select>
          </div>
        </div>
        
        <div>
          {hasPermission('report:export') && (
<button
            onClick={handleExport}
            className="flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors shadow-sm"
          >
            <Download className="w-4 h-4 mr-2" />
            Export Excel
          </button>
)}
        </div>
      </div>

      {/* Table Container */}
      <div className="flex-1 overflow-auto p-4 flex justify-center">
        <div className="inline-block align-middle bg-white pb-10">
          <table className="border-collapse border border-gray-400">
            <thead>
              <tr>
                <th colSpan={14} className="border border-gray-400 px-4 py-1 text-sm text-center font-normal uppercase bg-white">
                  PUNE CANTONMENT BOARD
                </th>
              </tr>
              <tr>
                <th colSpan={14} className="border border-gray-400 px-4 py-1 text-sm text-center font-normal uppercase bg-white">
                  FINAL SETTLEMENT FOR THE YEAR {startYear}-{endYear.toString().slice(-2)}
                </th>
              </tr>
              <tr>
                <th className={getThClass()}></th>
                <th className={getThClass()}>01-04-{startYear}</th>
                <th className={getThClass()}>01-05-{startYear}</th>
                <th className={getThClass()}>01-06-{startYear}</th>
                <th className={getThClass()}>01-07-{startYear}</th>
                <th className={getThClass()}>01-08-{startYear}</th>
                <th className={getThClass()}>01-09-{startYear}</th>
                <th className={getThClass()}>01-10-{startYear}</th>
                <th className={getThClass()}>01-11-{startYear}</th>
                <th className={getThClass()}>01-12-{startYear}</th>
                <th className={getThClass()}>01-01-{endYear}</th>
                <th className={getThClass()}>01-02-{endYear}</th>
                <th className={getThClass()}>01-03-{endYear}</th>
                <th className={getThClass()}>TOTAL</th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {filteredData.map((row, idx) => (
                <tr key={idx} className="hover:bg-gray-50">
                  <td className={getTdClass(false)}>{row.dept}</td>
                  <td className={getTdClass()}>{formatCell(row.apr)}</td>
                  <td className={getTdClass()}>{formatCell(row.may)}</td>
                  <td className={getTdClass()}>{formatCell(row.jun)}</td>
                  <td className={getTdClass()}>{formatCell(row.jul)}</td>
                  <td className={getTdClass()}>{formatCell(row.aug)}</td>
                  <td className={getTdClass()}>{formatCell(row.sep)}</td>
                  <td className={getTdClass()}>{formatCell(row.oct)}</td>
                  <td className={getTdClass()}>{formatCell(row.nov)}</td>
                  <td className={getTdClass()}>{formatCell(row.dec)}</td>
                  <td className={getTdClass()}>{formatCell(row.jan)}</td>
                  <td className={getTdClass()}>{formatCell(row.feb)}</td>
                  <td className={getTdClass()}>{formatCell(row.mar)}</td>
                  <td className={getTdClass()}>{formatCell(row.total)}</td>
                </tr>
              ))}
              
              {/* Total Row */}
              <tr className="bg-white">
                <td className="border border-gray-400 px-2 py-2 text-xs font-normal text-left uppercase">TOTAL</td>
                <td className={getTdClass()}>{formatCell(totals.apr)}</td>
                <td className={getTdClass()}>{formatCell(totals.may)}</td>
                <td className={getTdClass()}>{formatCell(totals.jun)}</td>
                <td className={getTdClass()}>{formatCell(totals.jul)}</td>
                <td className={getTdClass()}>{formatCell(totals.aug)}</td>
                <td className={getTdClass()}>{formatCell(totals.sep)}</td>
                <td className={getTdClass()}>{formatCell(totals.oct)}</td>
                <td className={getTdClass()}>{formatCell(totals.nov)}</td>
                <td className={getTdClass()}>{formatCell(totals.dec)}</td>
                <td className={getTdClass()}>{formatCell(totals.jan)}</td>
                <td className={getTdClass()}>{formatCell(totals.feb)}</td>
                <td className={getTdClass()}>{formatCell(totals.mar)}</td>
                <td className={getTdClass()}>{formatCell(totals.total)}</td>
              </tr>
              
            </tbody>
          </table>
          
          {/* Clean Footer Section Outside Table */}
          <div className="w-full flex flex-col h-48 bg-white">
            {/* Blank area below TOTAL */}
            <div className="flex-1"></div>
            
            {/* Signatures area */}
            <div className="h-24 flex">
              <div className="w-[30%] p-2 text-xs text-left">Prepared by</div>
              <div className="flex-1 p-2 text-xs text-left">Checked by</div>
              <div className="w-[25%] p-2 text-xs flex items-end justify-center pb-4">Chief Accountant</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinalSettlementView;
